/**
 * Restores the scroll position after navigating back.
 *
 * The back buttons navigate to their prevRoute via pushState (a new history entry), and pages
 * load their content async. So the browser can't restore anything itself, and after "back" you
 * end up at the top again. Here we save the position of the page being left on every pushState
 * and popstate, and restore it when going back to the page you just came from, or on browser
 * back/forward (popstate).
 * Because the content is still loading, we keep retrying for a few seconds until the position
 * holds; as soon as the user scrolls themselves, we stop.
 *
 * We hook into history.pushState instead of a single route function, because many code paths
 * call pushState themselves (aca-app, aca-training, aca-main-page, iron-location, React).
 */

const STORAGE_KEY = 'scrollPositions';
const MAX_ENTRIES = 50;
const MAX_BACK_STACK = 50;
const CHECK_INTERVAL_MS = 100;
const STABLE_CHECKS = 5;
const MAX_WAIT_MS = 4000;
const USER_INPUT_EVENTS = ['wheel', 'touchstart', 'mousedown', 'keydown'];

type Positions = Record<string, number>;

const installed = new WeakSet<Window>();

// Turns a URL into a storage key of pathname + query string, without origin and hash.
function toKey(win: Window, url: string | URL): string {
    const resolved = new URL(url, win.location.href);

    // example: training/123?id=5#section1 -> key: training/123?id=5
    return `${resolved.pathname}${resolved.search}`;
}

// Reads the saved scroll positions from sessionStorage; returns {} if missing, invalid or unavailable.
function readPositions(win: Window): Positions {
    try {
        return JSON.parse(win.sessionStorage.getItem(STORAGE_KEY) ?? '{}') ?? {};
    } catch {
        return {};
    }
}

// Saves the scroll position for a page and keeps only the MAX_ENTRIES most recent pages.
function savePosition(win: Window, key: string, top: number) {
    const positions = readPositions(win);

    // Re-insert so the most recent entry stays last in the order.
    delete positions[key];
    positions[key] = top;

    const keys = Object.keys(positions);

    keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES)).forEach((k) => delete positions[k]);

    try {
        win.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
    } catch {
        // Without sessionStorage (private mode, quota) we simply restore nothing.
    }
}

// Hooks into pushState, replaceState and popstate to save and restore scroll positions; returns a cleanup function.
export function installScrollRestoration(win: Window = window): () => void {
    if (installed.has(win)) return () => {};
    installed.add(win);

    const {history} = win;
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    const previousScrollRestoration = history.scrollRestoration;

    // The page currently shown; on popstate the URL has already changed, so we need this to save.
    let currentKey = toKey(win, win.location.href);
    // The pages you came from, most recent last; navigating to the last one counts as going back.
    // A stack rather than a single key, so re-opening the page you just went back from is forward.
    let backStack: string[] = [];
    let cancelRestore: (() => void) | null = null;

    const restore = (key: string) => {
        // Stops any restore attempt that is still running, so only one runs at a time.
        cancelRestore?.();

        const top = readPositions(win)[key];

        // Also restores 0, so a page you left at the top doesn't open halfway down.
        if (top === undefined) return;

        let elapsed = 0;
        let stable = 0;
        let timer = 0;

        // Ends this restore attempt: clears the interval, removes the input listeners and resets cancelRestore.
        const stop = () => {
            win.clearInterval(timer);
            USER_INPUT_EVENTS.forEach((type) => win.removeEventListener(type, stop, true));
            cancelRestore = null;
        };

        // Keeps scrolling back to the saved position while the page grows, until it holds or time runs out.
        timer = win.setInterval(() => {
            elapsed += CHECK_INTERVAL_MS;

            if (Math.abs(win.scrollY - top) > 1) {
                stable = 0;
                win.scrollTo(0, top);
            } else {
                stable += 1;
            }

            if (stable >= STABLE_CHECKS || elapsed >= MAX_WAIT_MS) stop();
        }, CHECK_INTERVAL_MS);

        // Stops restoring as soon as the user scrolls, clicks, taps or types, so we don't fight them.
        USER_INPUT_EVENTS.forEach((type) =>
            win.addEventListener(type, stop, {capture: true, passive: true}),
        );
        cancelRestore = stop;
    };

    // Wraps pushState: saves the scroll position of the page being left and restores it when going back.
    history.pushState = function (data: unknown, unused: string, url?: string | URL | null) {
        const fromKey = toKey(win, win.location.href);
        const toUrlKey = url == null ? fromKey : toKey(win, url);

        originalPushState.call(history, data, unused, url);

        if (toUrlKey === fromKey) return;

        savePosition(win, fromKey, win.scrollY);
        cancelRestore?.();
        currentKey = toUrlKey;

        if (toUrlKey === backStack[backStack.length - 1]) {
            backStack.pop();
            restore(toUrlKey);
            return;
        }

        backStack.push(fromKey);
        backStack = backStack.slice(-MAX_BACK_STACK);
    };

    // Wraps replaceState: same history entry, so nothing to save, but the current page's URL changes.
    history.replaceState = function (data: unknown, unused: string, url?: string | URL | null) {
        originalReplaceState.call(history, data, unused, url);
        currentKey = toKey(win, win.location.href);
    };

    // Browser back/forward: first save the page being left (the new page isn't rendered yet, so
    // scrollY still belongs to it), then clear the back stack (our place in the history is now
    // unknown) and restore the page we arrive at.
    const onPopState = () => {
        savePosition(win, currentKey, win.scrollY);
        currentKey = toKey(win, win.location.href);
        backStack = [];
        restore(currentKey);
    };

    // Otherwise the browser restores on popstate itself, immediately, on a page that is still too short.
    history.scrollRestoration = 'manual';
    win.addEventListener('popstate', onPopState);

    // Cleanup: undoes everything installed above, so it can be installed again (used by the tests).
    return () => {
        cancelRestore?.();
        history.pushState = originalPushState;
        history.replaceState = originalReplaceState;
        history.scrollRestoration = previousScrollRestoration;
        win.removeEventListener('popstate', onPopState);
        installed.delete(win);
    };
}
